import { profileApi } from 'api'
import { URLS } from 'constants/urls'
import { Confirmation } from 'ui/molecules/Confirmation'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MENU_NAMES } from 'texts/menuNames'
import {
  PROFILE_ARCHIVE,
  PROFILE_ARCHIVE_CONFIRMATION,
} from 'texts/profileArchive'
import { breadcrumbs } from 'constants/breadcrumbs'
import { Button } from '@frontend/design_system'
import { Container } from 'ui/molecules/Container'
import { RefreshIcon } from 'ui/icons'
import styles from './styles.module.scss'
import { ProfileDetailsFormWrapper } from 'modules/Profile/ProfileList/components/ProfileDetailsFormWrapper'
import { ProfileFormValues } from 'ui/components/ProfileDetailsForm/types'
import { Typography } from 'ui/atoms/Typography'
import { useApiRequest } from 'hooks/useApiRequest'
import { PROFILE_DETAILS_TITLE } from 'texts/profileDetails'
import { resetSelectedFilters } from 'redux/filters/slice'
import { useDispatch } from 'react-redux'
import { useCompanyId } from 'hooks/useCompanyId'

export const ProfileRestore = () => {
  const [initialData, setInitialData] = useState<Partial<ProfileFormValues>>({})
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  const dispatch = useDispatch()
  const companyId = useCompanyId()

  const { apiRequest } = useApiRequest(
    () => profileApi.restoreCompany(Number(companyId)),
    '',
    true
  )

  const getCompanyInfoRequest = useApiRequest((companyId) =>
    profileApi.getCompanyInfo(Number(companyId))
  )

  const breadcrumbList = [breadcrumbs.archive]

  const navigate = useNavigate()

  const handleRestoreCompany = () => {
    setIsConfirmationModalOpen(true)
  }

  const handleModalClose = () => {
    setIsConfirmationModalOpen(false)
  }

  const handleConfirm = async (): Promise<true | false> => {
    const response = await apiRequest()
    if (!response?.data) {
      setIsConfirmationModalOpen(false)
      return false
    } else {
      dispatch(resetSelectedFilters())
      return true
    }
  }

  const handleRedirectToSupplier = () => {
    navigate(`${URLS.PROFILE}${URLS.PROFILE_LIST}/${companyId}/edit`, {
      state: [
        {
          link: URLS.PROFILE + URLS.PROFILE_LIST,
          label: MENU_NAMES.PROFILE_LIST.NAME,
        },
      ],
    })
  }

  const handleSuccessModalClose = () =>
    navigate(`${URLS.PROFILE}${URLS.ARCHIVE}`)

  useEffect(() => {
    const getInfo = async () => {
      const response = await getCompanyInfoRequest.apiRequest(companyId)
      if (response?.data) {
        setInitialData({
          ...response.data.companyInfo,
          companyCountryID: String(response.data.companyInfo.companyCountryID),
          companyCurrencyID: String(
            response.data.companyInfo.companyCurrencyID
          ),
          logo: response.data.companyInfo.logo
            ? [
                {
                  ...new File(['a'], 'logo'),
                  url: response.data.companyInfo.logo,
                },
              ]
            : [],
        })
      }
    }
    getInfo()
  }, [])

  const header = (
    <>
      {isConfirmationModalOpen && (
        <Confirmation
          confirmModalProps={{
            question: `${PROFILE_ARCHIVE_CONFIRMATION.CONFIRMATION_MODAL.QUESTION} ${initialData?.companyName}?`,
            onConfirm: handleConfirm,
            onClose: handleModalClose,
            title: `${PROFILE_ARCHIVE.RESTORE} ${initialData.companyName}?`,
          }}
          successModalProps={{
            onClose: handleSuccessModalClose,
            title: PROFILE_ARCHIVE_CONFIRMATION.SUCCESS_MODAL.TITLE,
            buttonLabel:
              PROFILE_ARCHIVE_CONFIRMATION.SUCCESS_MODAL.BUTTON_LABEL,
            onConfirm: handleRedirectToSupplier,
            subTitle: `${initialData?.companyName} ${PROFILE_ARCHIVE_CONFIRMATION.SUCCESS_MODAL.SUBTITLE}`,
          }}
        />
      )}
      <Typography name="H5">{PROFILE_DETAILS_TITLE}</Typography>
    </>
  )
  return (
    <Container
      title={header}
      rightBlock={
        <div className={styles['button-wrapper']}>
          <Button
            typographyName="Button2"
            label={PROFILE_ARCHIVE.RESTORE}
            onClick={handleRestoreCompany}
            dataTestId="restore-button"
            iconRight={<RefreshIcon color="whiteMaster" />}
          />
        </div>
      }
      breadcrumbList={breadcrumbList}
      currentPageLabel={PROFILE_DETAILS_TITLE}
      loading={getCompanyInfoRequest.loading}
    >
      <ProfileDetailsFormWrapper initialValues={initialData} readOnly />
    </Container>
  )
}
